import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { messagingAPI } from '../../services/api';
import { getRandomLoadingMessage } from '../../utils/loadingMessages';
import '../../css/about.css';

const Messaging = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [newConversationSubject, setNewConversationSubject] = useState('');
  const [newConversationMessage, setNewConversationMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/signin', { state: { from: '/messages' } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchConversations();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Auto-scroll to bottom when messages change
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await messagingAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationMessages = async (conversationId) => {
    try {
      const response = await messagingAPI.getConversation(conversationId);
      setMessages(response.data.messages || []);
      setSelectedConversation(response.data);

      // Mark as read
      await messagingAPI.markAsRead(conversationId);

      // Refresh conversations list to update unread count
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSelectConversation = (conversation) => {
    fetchConversationMessages(conversation.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await messagingAPI.sendMessage(selectedConversation.id, newMessage);
      setNewMessage('');
      // Refresh messages
      fetchConversationMessages(selectedConversation.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    if (!newConversationSubject.trim() || !newConversationMessage.trim()) {
      alert('Please provide both a subject and initial message.');
      return;
    }

    setSending(true);
    try {
      const response = await messagingAPI.createConversation({
        subject: newConversationSubject,
        initial_message: newConversationMessage
      });

      // Reset form
      setNewConversationSubject('');
      setNewConversationMessage('');
      setShowNewConversation(false);

      // Refresh conversations and select new one
      await fetchConversations();
      fetchConversationMessages(response.data.id);
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to create conversation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return '#3b82f6';
      case 'in_progress':
        return 'var(--pink)';
      case 'resolved':
        return '#4ade80';
      case 'closed':
        return '#999';
      default:
        return '#999';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="about-page">
        <section className="about-section">
          <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h1>{getRandomLoadingMessage()}</h1>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="about-page">
      <section className="about-section">
        <div className="container" style={{ maxWidth: '1400px', padding: '20px' }}>
          <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>Messages</h1>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '350px 1fr',
            gap: '20px',
            height: 'calc(100vh - 200px)',
            minHeight: '600px'
          }}>
            {/* Conversations List */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, color: '#fff' }}>Conversations</h3>
                <button
                  onClick={() => setShowNewConversation(true)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#fff',
                    background: 'var(--pink)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'var(--violet)'}
                  onMouseLeave={(e) => e.target.style.background = 'var(--pink)'}
                >
                  + New
                </button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1 }}>
                {conversations.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#999' }}>
                    No conversations yet. Start a new conversation!
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        background: selectedConversation?.id === conv.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedConversation?.id !== conv.id) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedConversation?.id !== conv.id) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <h4 style={{
                          margin: 0,
                          color: '#fff',
                          fontSize: '1rem',
                          fontWeight: '600',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          paddingRight: '10px'
                        }}>
                          {conv.subject}
                        </h4>
                        {conv.unread_count > 0 && (
                          <span style={{
                            minWidth: '20px',
                            height: '20px',
                            padding: '0 6px',
                            borderRadius: '10px',
                            background: 'var(--pink)',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          background: `${getStatusColor(conv.status)}20`,
                          color: getStatusColor(conv.status)
                        }}>
                          {conv.status.replace('_', ' ')}
                        </span>
                        <span style={{ color: '#999', fontSize: '12px' }}>
                          {formatDate(conv.last_message_at || conv.updated_at)}
                        </span>
                      </div>
                      {conv.last_message && (
                        <p style={{
                          margin: '8px 0 0 0',
                          color: '#aaa',
                          fontSize: '13px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {conv.last_message.sender_is_staff ? '🎧 Team: ' : 'You: '}
                          {conv.last_message.content}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Messages View */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {showNewConversation ? (
                /* New Conversation Form */
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{
                    padding: '20px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff' }}>New Conversation</h3>
                    <button
                      onClick={() => {
                        setShowNewConversation(false);
                        setNewConversationSubject('');
                        setNewConversationMessage('');
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '14px',
                        color: '#ccc',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>

                  <form onSubmit={handleCreateConversation} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                        Subject
                      </label>
                      <input
                        type="text"
                        value={newConversationSubject}
                        onChange={(e) => setNewConversationSubject(e.target.value)}
                        placeholder="What is this conversation about?"
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                        Message
                      </label>
                      <textarea
                        value={newConversationMessage}
                        onChange={(e) => setNewConversationMessage(e.target.value)}
                        placeholder="Type your message here..."
                        required
                        style={{
                          flex: 1,
                          padding: '12px',
                          fontSize: '16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          boxSizing: 'border-box',
                          resize: 'none',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={sending}
                      style={{
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#fff',
                        background: sending ? 'rgba(255, 255, 255, 0.3)' : 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: sending ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(216, 134, 255, 0.4)'
                      }}
                    >
                      {sending ? 'Creating...' : 'Start Conversation'}
                    </button>
                  </form>
                </div>
              ) : selectedConversation ? (
                /* Selected Conversation View */
                <>
                  <div style={{
                    padding: '20px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>{selectedConversation.subject}</h3>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      background: `${getStatusColor(selectedConversation.status)}20`,
                      color: getStatusColor(selectedConversation.status)
                    }}>
                      {selectedConversation.status.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Messages */}
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}>
                    {messages.map((message) => {
                      const isYou = message.sender === user?.id;
                      const isStaff = message.sender_is_staff;

                      return (
                        <div
                          key={message.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: isYou ? 'flex-end' : 'flex-start',
                            maxWidth: '70%',
                            alignSelf: isYou ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div style={{
                            background: isYou ? 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)' : 'rgba(255, 255, 255, 0.1)',
                            padding: '12px 16px',
                            borderRadius: isYou ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            wordBreak: 'break-word',
                            boxShadow: isYou ? '0 2px 8px rgba(216, 134, 255, 0.3)' : 'none'
                          }}>
                            {!isYou && (
                              <div style={{
                                fontSize: '12px',
                                color: 'var(--pink)',
                                fontWeight: 'bold',
                                marginBottom: '4px'
                              }}>
                                {isStaff ? '🎧 UnsupervisedBias Team' : message.sender_name}
                              </div>
                            )}
                            <div style={{ color: '#fff', fontSize: '15px', lineHeight: '1.5' }}>
                              {message.content}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#999',
                            marginTop: '4px',
                            paddingLeft: isYou ? 0 : '8px',
                            paddingRight: isYou ? '8px' : 0
                          }}>
                            {formatDate(message.created_at)}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} style={{
                    padding: '20px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    gap: '12px'
                  }}>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      disabled={sending}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        fontSize: '15px',
                        borderRadius: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      style={{
                        padding: '12px 24px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        color: '#fff',
                        background: (sending || !newMessage.trim()) ? 'rgba(255, 255, 255, 0.2)' : 'var(--pink)',
                        border: 'none',
                        borderRadius: '24px',
                        cursor: (sending || !newMessage.trim()) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!sending && newMessage.trim()) {
                          e.target.style.background = 'var(--violet)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!sending && newMessage.trim()) {
                          e.target.style.background = 'var(--pink)';
                        }
                      }}
                    >
                      Send
                    </button>
                  </form>
                </>
              ) : (
                /* No Conversation Selected */
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  padding: '40px'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>💬</div>
                  <h3 style={{ color: '#ccc', marginBottom: '12px' }}>No conversation selected</h3>
                  <p style={{ textAlign: 'center', lineHeight: '1.6' }}>
                    Select a conversation from the list or start a new one to begin messaging
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Messaging;
